import { useState, useEffect } from 'react';
import useProfileCommonStore from '../use-profile-common-store';
import moment from 'moment-msdate';
import _ from 'lodash';
import { Crosshair, ModchartsInteractive } from '@markit/modcharts';
import chartHelpers from '../../../utils/chart-helpers/index';

import {
  useProfileHypotheticalGrowth,
  useProfilePriceChart,
  useProfilePriceChartDailyData,
  useQuote
} from '../../../hooks/api';
import format from '../../../utils/format';
import { isCIT } from '../../../utils/fund-type-helpers/fundTypeHelpers';

const dropdownItems = [
  {
    label: '3 Month',
    value: '3m',
    monthValue: 3,
    dayValue: 90,
    dataPeriod: 'Day'
  },
  {
    label: '6 Month',
    value: '6m',
    monthValue: 6,
    dayValue: 180,
    dataPeriod: 'Day'
  },
  {
    label: '1 Year',
    value: '1y',
    monthValue: 12,
    dayValue: 365,
    dataPeriod: 'Month'
  },
  {
    label: '3 Years',
    value: '3y',
    monthValue: 36,
    dayValue: 1095,
    dataPeriod: 'Month'
  },
  {
    label: '5 Years',
    value: '5y',
    monthValue: 60,
    dayValue: 1825,
    dataPeriod: 'Month'
  },
  {
    label: '10 Years',
    value: '10y',
    monthValue: 120,
    dayValue: 3650,
    dataPeriod: 'Month'
  }
].map(item => ({
  ...item,
  limit: moment().subtract(item.monthValue, 'months')
}));

const CHART_TYPES = [
  { label: 'Growth of $10k', value: 'growthOf10k' },
  { label: 'Price Chart', value: 'priceChart' }
];

const getSrTableData = (fund = { growth: [] }) => {
  return {
    headers: [fund.name],
    rows: fund.growth
  };
};

const formatColumnTitle = (startDate, endDate, period) => {
  const columnStartDate = new Date(startDate).toISOString();
  const columnEndDate = new Date(endDate).toISOString();
  let columnTitle = '';
  if (columnStartDate === columnEndDate) {
    columnTitle = `${format.date(columnStartDate, {
      formatString: period === 'Day' ? '' : 'MM/YYYY'
    })}`;
  } else {
    columnTitle = `${format.date(columnStartDate, {
      formatString: period === 'Day' ? '' : 'MM/YYYY'
    })} - ${format.date(columnEndDate, {
      formatString: period === 'Day' ? '' : 'MM/YYYY'
    })}`;
  }
  return columnTitle;
};

const formatChartTableData = (column, row, data, dataPeriod) => {
  if (!data) return {};
  const chunkedData = _.chunk(data, row);
  const result = [];
  const lastChunkLen =
    chunkedData.length - 1 < 0 ? 0 : chunkedData[chunkedData.length - 1].length;

  chunkedData.forEach(item => {
    result.push({
      title: formatColumnTitle(
        item[0].date,
        item[row - 1] ? item[row - 1].date : item[lastChunkLen - 1].date,
        dataPeriod
      ),
      data: item
    });
  });
  return _.chunk(result, column);
};

const useHypotheticalGrowth = ({ showPriceChartOnly }) => {
  const [chartTableIndex, setChartTableIndex] = useState(0);
  const [chartSelected, setChartSelected] = useState(CHART_TYPES[0]);
  const [chartWidth, setChartWidth] = useState();
  const [currentTimeFrame, setCurrentTimeFrame] = useState(dropdownItems[3]);
  const [dataPeriod, setDataPeriod] = useState('Month');
  const [hypoChartObject, setHypoChartObject] = useState();
  const [isTimeFrameLoaded, setIsTimeFrameLoaded] = useState(false);
  const [numberOfDays, setNumberOfDays] = useState(1095);
  const [priceChartObject, setPriceChartObject] = useState();
  const [showAsTable, setShowAsTable] = useState(false);
  const [timeFrame, setTimeFrame] = useState('SI');
  const [crossHairData, setCrossHairData] = useState();

  const { apiConfigBase, commonState: { xref } } = useProfileCommonStore();
  const { client, xid } = { ...apiConfigBase };
  const fundType = xref?.data?.fundType;
  const isProprietaryFund = xref?.data?.isProprietaryFund;
  const isCITFund = isCIT(isProprietaryFund, fundType);

  const isHypo10k =
    chartSelected.value === 'growthOf10k' && !showPriceChartOnly;

  const handleChartResize = width => {
    setChartWidth(width);
  };

  const handleTimeframeChange = newTimeframe => {
    setCurrentTimeFrame(newTimeframe);
    setTimeFrame(newTimeframe.value);
    setNumberOfDays(newTimeframe.dayValue);
    setDataPeriod(showAsTable ? newTimeframe.dataPeriod : 'Day');
    setChartTableIndex(0);
  };

  const handleChartSwitch = selectedChart => {
    setChartSelected(selectedChart);
    setChartTableIndex(0);
  };

  const handleShowAsTableChange = ifShowAsTable => {
    setDataPeriod(ifShowAsTable ? currentTimeFrame.dataPeriod : 'Day');
    setShowAsTable(ifShowAsTable);
  };

  if (!chartWidth && isTimeFrameLoaded) {
    const hypoGrowthChart =
      document.getElementById(
        isHypo10k ? 'hypo-chart-target' : 'price-chart-target'
      ) || {};
    const width = hypoGrowthChart.offsetWidth || 200;
    setChartWidth(width);
  }

  const tableInputs = {
    client,
    isCITFund,
    timeFrame,
    xid
  };

  const data = {};
  const fundsData = useProfileHypotheticalGrowth(tableInputs);
  const quoteData = useQuote(apiConfigBase);
  const priceChartData = useProfilePriceChart({
    xid,
    numberOfDays,
    client,
    shouldLoad: true
  });

  //It will use for export excel file. daily data only
  const priceChartDailyData = useProfilePriceChartDailyData({
    xid,
    numberOfDays,
    client,
    shouldLoad: true,
    isDaily: true
  });

  const priceData =
    priceChartData && priceChartData.isLoaded && priceChartData.data
      ? priceChartData.data
      : [];

  let srHypo10kTableData = [];

  let desktopChartData;
  let mobileChartData;
  let priceDailyDataForXLS;
  let fundSpecificDropdownItems = [];
  const loadTimeFrame = () => {
    const inceptionDate = quoteData.data.inceptionDate;
    const momentInception = moment(inceptionDate);

    fundSpecificDropdownItems = dropdownItems
      .filter(item => item.limit.isAfter(momentInception))
      .map(({ limit, ...rest }) => rest);

    const isLessThanTenYearsOld = momentInception.isAfter(
      moment().subtract(10, 'years')
    );

    if (isLessThanTenYearsOld) {
      fundSpecificDropdownItems.push({
        label: 'Since Inception',
        value: 'SI',
        dayValue: 9999,
        dataPeriod: 'Month'
      });
    }

    if (!isTimeFrameLoaded) {
      const isMoreThanThreeYearsOld = momentInception.isBefore(
        moment().subtract(3, 'years')
      );

      if (isMoreThanThreeYearsOld) {
        setTimeFrame('3y');
      }
      setIsTimeFrameLoaded(true);
    }
  };

  if (fundsData.isLoaded && quoteData.isLoaded && isHypo10k) {
    if (!hypoChartObject) {
      const hypoChart = new ModchartsInteractive('#hypo-chart-target');
      setHypoChartObject(hypoChart);
      const crosshair = new Crosshair(hypoChart, {
        enabled: true
      });
      setCrossHairData(crosshair);
    }
    loadTimeFrame();

    const mainFundData = fundsData.data.funds?.find(fund => fund?.recordType === 'fund' || fund?.recordType?.toLowerCase() === 'growthof10kfund') ?? {};

    srHypo10kTableData = getSrTableData(mainFundData);

    data.endDate = _.get(_.last(mainFundData.growth), 'date');
    data.startDate = _.get(_.first(mainFundData.growth), 'date');

    /* eslint-disable indent */
    data.fundsData = fundsData.data.funds
      ? fundsData.data.funds.map(fund => ({
        chartColor: fund.chartColor,
        //grab most recent value for table display
        value: _.last(fund.growth).value,
        name: fund.name,
        legendName: fund.legendName
      }))
      : [{}];
    /* eslint-enable indent */

    data.fundsAsOfDate = fundsData.data.asOfDate;
    data.closeProximityDisclosure = fundsData.data.closeProximityDisclosure;
  }

  if (
    priceChartData &&
    priceChartData.isLoaded &&
    quoteData.isLoaded &&
    !isHypo10k
  ) {
    if (!priceChartObject) {
      const priceChart = new ModchartsInteractive('#price-chart-target');
      setPriceChartObject(priceChart);
      const crosshair = new Crosshair(priceChart, {
        enabled: true
      });
      setCrossHairData(crosshair);
    }
    loadTimeFrame();
  }

  const currentTimeframeOption = {
    ...fundSpecificDropdownItems.find(item => item.value === timeFrame)
  };

  if (showAsTable) {
    desktopChartData = formatChartTableData(
      2,
      9,
      !isHypo10k && priceChartData.isLoaded
        ? priceChartData.data
        : srHypo10kTableData.rows,
      isHypo10k ? 'Month' : dataPeriod
    );
    mobileChartData = formatChartTableData(
      1,
      6,
      !isHypo10k && priceChartData.isLoaded
        ? priceChartData.data
        : srHypo10kTableData.rows,
      isHypo10k ? 'Month' : dataPeriod
    );
    priceDailyDataForXLS = formatChartTableData(
      1,
      6,
      !isHypo10k && priceChartDailyData?.isLoaded
        ? priceChartDailyData.data
        : srHypo10kTableData.rows,
      'day'
    );
  }

  const loadChart = ({ funds, type, chartObject }) => {
    const baseChartParams = chartHelpers.formatBaseChartParams(funds, type);
    chartObject.load(baseChartParams);
  };

  useEffect(() => {
    if (showAsTable) return;

    const handleHypoChart = () => {
      if (
        fundsData.isLoaded &&
        chartSelected.value !== 'priceChart' &&
        !showPriceChartOnly &&
        hypoChartObject
      ) {
        const funds = fundsData.data?.funds;
        if (funds) {
          loadChart({
            funds,
            type: 'hypo-chart',
            chartObject: hypoChartObject
          });
        }
      }
    };

    const handlePriceChart = () => {
      if (
        priceChartData?.isLoaded &&
        (showPriceChartOnly || chartSelected.value === 'priceChart') &&
        priceChartObject
      ) {
        if (priceData) {
          loadChart({
            funds: { fundName: quoteData.data?.fundName, price: priceData },
            type: 'price-chart',
            chartObject: priceChartObject
          });
        }
      }
    };

    const handleAriaAttributes = () => {
      const hypoGrowthChart = document.getElementById('growth-and-price-chart-target');
      const rootOverlay = hypoGrowthChart?.getElementsByClassName('modcharts-rootoverlay');
      if (!_.isEmpty(rootOverlay)) {
        rootOverlay[0].setAttribute('aria-label', 'chart label');
        rootOverlay[0].setAttribute('role', 'img');
      }
    };

    handleHypoChart();
    handlePriceChart();
    handleAriaAttributes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTimeFrame,
    showAsTable,
    fundsData.isLoaded,
    fundsData.data,
    hypoChartObject,
    isHypo10k,
    chartSelected,
    priceData,
    showPriceChartOnly
  ]);

  return {
    chartHeight: !isHypo10k ? 370 : 300,
    chartSelected,
    chartTableIndex,
    chartTypes: CHART_TYPES,
    closeProximityDisclosure: data.closeProximityDisclosure,
    crossHairData,
    currentTimeframeOption,
    dropdownItems: fundSpecificDropdownItems,
    desktopChartData,
    priceDailyDataForXLS,
    endDate: data.endDate,
    fundsData: data.fundsData,
    fundsAsOfDate: data.fundsAsOfDate,
    handleChartTableIndexSelect: setChartTableIndex,
    hypoChartObject,
    priceChartObject,
    isHypo10k,
    mobileChartData,
    onChartChange: handleChartSwitch,
    onChangeTimeframe: handleTimeframeChange,
    onResizeChart: handleChartResize,
    onShowAsTableClick: handleShowAsTableChange,
    priceChartLastClose:
      priceData.length > 0 ? priceData[priceData.length - 1] : undefined,
    priceData,
    quoteData,
    screenReaderTableData: srHypo10kTableData,
    showAsTable,
    startDate: data.startDate
  };
};

export default useHypotheticalGrowth;
